import React, { useState } from "react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { Waveform, StaticWaveform } from "@/components/landing/Waveform";
import { ScribbleCircle, Sparkle, BnGlyph } from "@/components/landing/Decorations";
import { RevealHeading, StaggerReveal } from "@/components/landing/Motion";
import { Play, Pause, Globe } from "lucide-react";

const VOICES = [
    {id: "noor", name: "Noor", gender: "F", accent: "Dhaka", desc: "Warm, news anchor.", color: "#FF5B3E"},
    {id: "rajib", name: "Rajib", gender: "M", accent: "Dhaka", desc: "Confident, editorial.", color: "#3D5BFF"},
    {id: "tara", name: "Tara", gender: "F", accent: "Kolkata", desc: "Bright, conversational.", color: "#2EE8B5"},
    {id: "arnab", name: "Arnab", gender: "M", accent: "Kolkata", desc: "Deep, documentary.", color: "#7B3FE4"},
    {id: "mira", name: "Mira", gender: "F", accent: "Dhaka", desc: "Soft, lifestyle.", color: "#FF4FA3"},
    {id: "sohel", name: "Sohel", gender: "M", accent: "Chittagong", desc: "Friendly, sports.", color: "#FFD23F"},
    {id: "pratibha", name: "Pratibha", gender: "F", accent: "Kolkata", desc: "Intellectual, longform.", color: "#FF7A60"},
    {id: "kabir", name: "Kabir", gender: "M", accent: "Dhaka", desc: "Calm, narrative.", color: "#00C4A7"},
    {id: "nusrat", name: "Nusrat", gender: "F", accent: "Kolkata", desc: "Lively, bright.", color: "#E040FB"},
    {id: "farhan", name: "Farhan", gender: "M", accent: "Sylhet", desc: "Energetic, engaging.", color: "#4CAF50"},
];

const VoiceCard = ({ v, playing, onToggle }) => (
    <div data-testid={`voice-${v.id}`} className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-all hover:-translate-y-1">
        <div className="flex items-start justify-between mb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-display text-[24px] font-bold text-white" style={{background: v.color}}>
                {v.name[0]}
            </div>
            <button onClick={onToggle} className="w-10 h-10 rounded-full bg-ink hover:bg-coral text-cream-100 flex items-center justify-center transition-colors">
                {playing ? <Pause size={14} fill="currentColor"/> : <Play size={14} className="ml-0.5" fill="currentColor"/>}
            </button>
        </div>
        <h3 className="font-display text-[28px] font-semibold tracking-tight text-ink leading-none">{v.name}</h3>
        <p className="text-[12px] text-ink-muted mt-1 mb-4">{v.gender === "F" ? "Female" : "Male"} · {v.accent}</p>
        <p className="text-[14px] text-ink leading-relaxed mb-4">{v.desc}</p>
        <div className="h-8 flex items-center">
            {playing ? <Waveform bars={28} color={v.color} height={28}/> : <StaticWaveform bars={28} color="#0A0A0A" height={28}/>}
        </div>
    </div>
);

export default function VoicesPage() {
    const [playingId, setPlayingId] = useState(null);
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        if (playingId) {
            const url = `/demos/demo_${playingId}.wav?v=2`;
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => setPlayingId(null);
            audio.play().catch(() => setPlayingId(null));
        }
    }, [playingId]);

    return (
        <div className="paper-grain min-h-screen">
            <Nav/>
            <main className="pt-32 pb-24 relative overflow-hidden">
                <ScribbleCircle color="#FFD23F" className="absolute top-20 right-[6%] w-32 h-32 opacity-50 animate-float-slow"/>
                <BnGlyph glyph="কণ্ঠ" className="absolute -left-2 top-24 text-[160px] lg:text-[260px] leading-none font-bold opacity-[0.05] select-none" color="#FF5B3E"/>
                <Sparkle color="#3D5BFF" className="absolute top-1/3 left-[10%] w-9 h-9 animate-float-fast"/>

                <div className="mx-auto max-w-[1240px] px-5 sm:px-8 relative">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-5">Voice library</p>
                    <RevealHeading as="h1" className="h-display text-[48px] sm:text-[72px] lg:text-[112px] text-ink max-w-[900px]">
                        Eight voices. <span className="italic-display text-coral">Many feelings.</span>
                    </RevealHeading>
                    <p className="mt-6 max-w-[640px] text-[16px] sm:text-[18px] text-ink-muted">
                        Trained on Dhaka, Chittagong, and Kolkata Bengali. Switch voices per article — or per section. New voices ship every quarter.
                    </p>

                    <div className="mt-8 inline-flex items-center gap-2 bg-cream-200 border border-cream-300 rounded-full px-4 py-2">
                        <Globe size={14} className="text-ink-muted"/>
                        <span className="text-[13px] text-ink-muted">Custom voice cloning available on Business + Enterprise.</span>
                    </div>

                    <StaggerReveal className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {VOICES.map(v => (
                            <div data-stagger key={v.id}>
                                <VoiceCard v={v} playing={playingId === v.id} onToggle={() => setPlayingId(playingId === v.id ? null : v.id)}/>
                            </div>
                        ))}
                    </StaggerReveal>
                </div>
            </main>
            <Footer/>
        </div>
    );
}
